import { FlatTreeControl } from '@angular/cdk/tree';
import { MatTreeFlatDataSource, MatTreeFlattener } from '@angular/material/tree';
import { PSTFieldNames } from '@common/data-models/fields/pst-field-names';
import { RecordComparatorHelpers, ResultGroup } from '@common/data-models/search-results/result-group';
import { TreeLikeFlatNode, TreeLikeNode } from '@common/data-models/search-results/search-results';
import { selectCurrentGroupBy, selectCurrentViewState, selectGroupByDateTimeField } from '@common/store/navigation/selectors/viewstate.selectors';
import { createFeatureSelector, createSelector } from '@ngrx/store';
import { DateTime } from '@src/suite/common/data-models/complex-types/date-time';
import { TileColumn } from '@src/suite/common/data-models/misc/tile-column';
import { TileDay } from '@src/suite/common/data-models/misc/tile-day';
import { FilterDef } from '@src/suite/common/data-models/navigation/filter-def';
import { PublicationViewStateRecord } from '@src/suite/common/data-models/records/publication-viewstate-record';
import { SingleItemViewStateRecord } from '@src/suite/common/data-models/records/single-item-viewstate-record';
import { SingleViewStateRecord } from '@src/suite/common/data-models/records/single-viewstate-record';
import { TopicViewStateRecord } from '@src/suite/common/data-models/records/topic-viewstate-record';
import {
    CARD_HEIGHT,
    CARD_LAST_ITEM_HEIGHT,
    CONDENSED_CARD_HEIGHT,
    CONDENSED_FIRST_ITEM_HEADER_HEIGHT,
    CONDENSED_LAST_ITEM_HEIGHT,
    CUSTOM_FIELDS_ADDED_HEIGHT,
    GROUP_HEADER_HEIGHT,
} from '@src/suite/common/enums/constants';
import { ObjectTypes } from '@src/suite/common/enums/object-types';
import { ViewStateLayouts } from '@src/suite/common/enums/viewstate-layouts';
import { ViewStateTypesEnum } from '@src/suite/common/enums/viewstate-types';
import { selectClientTimeZone, selectCustomFilterDefsDict, selectDateTimeFields, selectGroupSortDefs, selectPersonalTimeZone } from '@src/suite/common/store/global-tables/global-tables.selectors';
import { selectDayOfWeek } from '@src/suite/common/store/hello-results/hello-results.selectors';
import { selectI18N } from '@src/suite/common/store/i18n/selectors/i18n.selectors';
import { selectCurrentNodeUuid, selectListGuids, selectListNewGuids, selectNodeExpandState } from '@src/suite/common/store/list/list.selectors';
import { selectCurrentApplication } from '@src/suite/common/store/navigation/selectors/navigation.selectors';
import {
    selectAssignmentRecordsMap,
    selectRundownRecordsMap,
    selectStoryRecordsMap,
    selectTopicRecordsMap,
    selectTopicsWithAssignmentsAndStories,
    selectUserRecords,
    selectUserRecordsMap,
} from '@src/suite/common/store/records/records.selectors';
import { extractLowestNodeLevel } from '@src/suite/common/utils/store-utils';
import { selectTreeDataSourceControlPair as selectLiveTreeDataSourceControlPair } from '@src/suite/live/store/selectors/live.selectors';
import { selectTreeDataSourceControlPair as selectCastTreeDataSourceControlPair } from '@suite/common/store/cast/cast.selectors';
import { selectFullscreenTreeDataSourceControlPair } from '@suite/common/store/fullscreen/selectors/fullscreen.selectors';
import moment from 'moment';

import { PlaybookState } from './playbook.models';
import {
    getAssignmentsTreeLikeData,
    getIsNodeExpandable,
    getNodeChildren,
    getNodeLevel,
    getObjectTypeByViewStateType,
    getSearchResultsTreeLikeData,
    getStoriesTreeLikeData,
    getTopicsTreeLikeData,
    nodeTransformer,
} from './playbook.utils';

const selectPlaybookState = createFeatureSelector<PlaybookState>('playbook');

export const selectStartEndIndex = createSelector(selectPlaybookState, state => {
    return { startIndex: state.StartIndex, endIndex: state.EndIndex };
});

export const selectGroupedSortedTopics = createSelector(
    selectCurrentViewState,
    selectGroupSortDefs,
    selectListGuids,
    selectTopicsWithAssignmentsAndStories,
    (currentViewState, groupSortDefs, listGuids, topicRecords) => {
        const groupBy = (currentViewState as SingleViewStateRecord)?.viewStateGrouping?.Value || PSTFieldNames.TargetPublicationDate;
        const groupByOrder = (currentViewState as SingleViewStateRecord)?.viewStateGroupSortOrder || 'asc';
        const sortBy = (currentViewState as SingleViewStateRecord)?.viewStateSorting?.Value || PSTFieldNames.TargetPublicationDate;
        if (sortBy === PSTFieldNames.PSTTopicOrder) {
            RecordComparatorHelpers.VSGuid = currentViewState.guid;
        } else {
            RecordComparatorHelpers.VSGuid = null;
        }
        const sortByOrder = (currentViewState as SingleViewStateRecord)?.viewStateSortOrder || 'asc';
        const topicGroup = new ResultGroup<StorytellingTopicRecord>();
        const topicGroupingDef = groupSortDefs?.[groupBy];
        const topicSortingDef = groupSortDefs?.[sortBy];
        const topics = listGuids.map(guid => topicRecords.get(guid) || null).filter(val => !!val);
        if (!topicGroupingDef || !topicSortingDef) {
            return topicGroup;
        }

        if (
            !(
                currentViewState?.viewStateType === ViewStateTypesEnum.TOPIC ||
                (currentViewState?.viewStateType === ViewStateTypesEnum.SINGLEITEM && (<SingleItemViewStateRecord>currentViewState)?.itemType === ObjectTypes.TOPIC)
            )
        ) {
            return null;
        }

        for (const topic of topics) {
            topicGroup.addToGroups(topicGroupingDef.valueConvertor(topic), topic);
        }
        topicGroup.sort(topicGroupingDef.groupKeyComparator(groupByOrder), topicSortingDef.recordComparator(sortByOrder));
        return topicGroup;
    }
);

export const selectGroupedSortedStories = createSelector(
    selectCurrentViewState,
    selectGroupSortDefs,
    selectListGuids,
    selectStoryRecordsMap,
    (currentViewState, groupSortDefs, listGuids, storyRecords) => {
        const storyGroup = new ResultGroup<StorytellingStoryRecord>();
        const groupBy = (currentViewState as SingleViewStateRecord)?.viewStateGrouping?.Value || PSTFieldNames.TargetPublicationDate;
        const groupByOrder = (currentViewState as SingleViewStateRecord)?.viewStateGroupSortOrder || 'asc';
        const sortBy = (currentViewState as SingleViewStateRecord)?.viewStateSorting?.Value || PSTFieldNames.TargetPublicationDate;
        const sortByOrder = (currentViewState as SingleViewStateRecord)?.viewStateSortOrder || 'asc';
        const storyGroupingDef = groupSortDefs?.[groupBy];
        const storySortingDef = groupSortDefs?.[sortBy];
        if (
            !(
                currentViewState?.viewStateType === ViewStateTypesEnum.SCRIPT ||
                (currentViewState?.viewStateType === ViewStateTypesEnum.SINGLEITEM && (<SingleItemViewStateRecord>currentViewState)?.itemType === ObjectTypes.SCRIPT)
            )
        ) {
            return null;
        }
        if (!storyGroupingDef || !storySortingDef) {
            return storyGroup;
        }
        const uniqueListIds = [...new Set(listGuids)];
        const stories = uniqueListIds.map(guid => storyRecords.get(guid) || null).filter(val => !!val);
        for (const topic of stories) {
            storyGroup.addToGroups(storyGroupingDef.valueConvertor(topic), topic);
        }
        storyGroup.sort(storyGroupingDef.groupKeyComparator(groupByOrder), storySortingDef.recordComparator(sortByOrder));
        return storyGroup;
    }
);

export const selectGroupedSortedAssignments = createSelector(
    selectCurrentViewState,
    selectGroupSortDefs,
    selectListGuids,
    selectAssignmentRecordsMap,
    (currentViewState, groupSortDefs, listGuids, assignmentRecords) => {
        const assignmentGroup = new ResultGroup<StorytellingAssignmentRecord>();
        const groupBy = (currentViewState as SingleViewStateRecord)?.viewStateGrouping?.Value || PSTFieldNames.TargetPublicationDate;
        const groupByOrder = (currentViewState as SingleViewStateRecord)?.viewStateGroupSortOrder || 'asc';
        const sortBy = (currentViewState as SingleViewStateRecord)?.viewStateSorting?.Value || PSTFieldNames.TargetPublicationDate;
        const sortByOrder = (currentViewState as SingleViewStateRecord)?.viewStateSortOrder || 'asc';
        const assignmentGroupingDef = groupSortDefs?.[groupBy];
        const assignmentSortingDef = groupSortDefs?.[sortBy];
        if (
            !(
                currentViewState?.viewStateType === ViewStateTypesEnum.ASSIGNMENT ||
                (currentViewState?.viewStateType === ViewStateTypesEnum.SINGLEITEM && (<SingleItemViewStateRecord>currentViewState)?.itemType === ObjectTypes.ASSIGNMENT)
            )
        ) {
            return null;
        }
        if (!assignmentGroupingDef || !assignmentSortingDef) {
            return assignmentGroup;
        }
        const assignments = listGuids.map(guid => assignmentRecords.get(guid) || null).filter(val => !!val);
        for (const topic of assignments) {
            assignmentGroup.addToGroups(assignmentGroupingDef.valueConvertor(topic), topic);
        }
        assignmentGroup.sort(assignmentGroupingDef.groupKeyComparator(groupByOrder), assignmentSortingDef.recordComparator(sortByOrder));
        return assignmentGroup;
    }
);

export const selectSearchItems = createSelector(
    selectCurrentViewState,
    selectGroupSortDefs,
    selectListGuids,
    selectTopicsWithAssignmentsAndStories,
    selectAssignmentRecordsMap,
    selectStoryRecordsMap,
    (currentViewState, groupSortDefs, listGuids, topicsWithAssignmentsAndStories, assignments, stories) => {
        const topicRecordSearchGroup = new ResultGroup<StorytellingTopicRecord>();
        const assignmentRecordSearchGroup = new ResultGroup<StorytellingAssignmentRecord>();
        const storyRecordSearchGroup = new ResultGroup<StorytellingStoryRecord>();
        const sortBy = (currentViewState as SingleViewStateRecord)?.viewStateSorting?.Value || PSTFieldNames.TargetPublicationDate;
        const sortByOrder = (currentViewState as SingleViewStateRecord)?.viewStateSortOrder || 'asc';
        const recordSortingDef = groupSortDefs?.[sortBy];
        if (currentViewState?.viewStateType !== ViewStateTypesEnum.SEARCH) {
            return null;
        }
        if (!recordSortingDef) {
            return topicRecordSearchGroup || assignmentRecordSearchGroup;
        }
        if (topicRecordSearchGroup) {
            const records = listGuids.map(guid => topicsWithAssignmentsAndStories.get(guid) || null).filter(val => !!val);
            for (const topic of records) {
                topicRecordSearchGroup.addToGroups(groupSortDefs?.SingleGroup.valueConvertor(topic), topic);
            }
            topicRecordSearchGroup.sort(groupSortDefs?.SingleGroup.groupKeyComparator(sortByOrder), recordSortingDef.recordComparator(sortByOrder));
        }
        if (assignmentRecordSearchGroup) {
            const records = listGuids.map(guid => assignments.get(guid) || null).filter(val => !!val);
            for (const assignment of records) {
                assignmentRecordSearchGroup.addToGroups(groupSortDefs?.Title.valueConvertor(assignment), assignment);
            }
            assignmentRecordSearchGroup.sort(groupSortDefs?.Title.groupKeyComparator(sortByOrder), recordSortingDef.recordComparator(sortByOrder));
        }
        if (storyRecordSearchGroup) {
            const records = listGuids.map(guid => stories.get(guid) || null).filter(val => !!val);
            for (const story of records) {
                storyRecordSearchGroup.addToGroups(groupSortDefs?.Title.valueConvertor(story), story);
            }
            storyRecordSearchGroup.sort(groupSortDefs?.Title.groupKeyComparator(sortByOrder), recordSortingDef.recordComparator(sortByOrder));
        }
        return { topicRecordSearchGroup, assignmentRecordSearchGroup, storyRecordSearchGroup };
    }
);

export const selectCurrentGroupSortedItems = createSelector(
    selectGroupedSortedTopics,
    selectGroupedSortedAssignments,
    selectGroupedSortedStories,
    selectSearchItems,
    (topics, assignments, stories, searchRecords) => {
        return topics || assignments || stories || searchRecords;
    }
);

export const selectTreeDataSourceControlPair = createSelector(
    selectCurrentViewState,
    selectCurrentGroupSortedItems,
    selectNodeExpandState,
    selectLiveTreeDataSourceControlPair,
    selectCastTreeDataSourceControlPair,
    selectFullscreenTreeDataSourceControlPair,
    (currentVs, records: ResultGroup<StorytellingRecord>, nodesExpandState, liveTreeData, castTreeData, fullscreenTreeData) => {
        if (!currentVs) return {};
        if (currentVs?.viewStateType === ViewStateTypesEnum.ROLIST) {
            return liveTreeData;
        }

        if (currentVs instanceof PublicationViewStateRecord) {
            // TODO: for now we support fullscreen treedata only for cast
            if (fullscreenTreeData) {
                return fullscreenTreeData;
            }
            return castTreeData;
        }

        const treeControl: FlatTreeControl<TreeLikeFlatNode<StorytellingRecord>, string> = new FlatTreeControl<TreeLikeFlatNode<StorytellingRecord>, string>(getNodeLevel, getIsNodeExpandable, {
            trackBy: node => node.uniqueId,
        });
        const treeFlattener = new MatTreeFlattener<TreeLikeNode<StorytellingRecord>, TreeLikeFlatNode<StorytellingRecord>, string>(nodeTransformer, getNodeLevel, getIsNodeExpandable, getNodeChildren);
        // Populates our flattened data into the tree control
        const treeDataSource = new MatTreeFlatDataSource(treeControl, treeFlattener);

        let vsType = currentVs?.viewStateType;
        if (currentVs?.viewStateType === ViewStateTypesEnum.SINGLEITEM) {
            const objectType = (<SingleItemViewStateRecord>currentVs)?.itemType;
            switch (objectType) {
                case ObjectTypes.ASSIGNMENT:
                    vsType = ViewStateTypesEnum.ASSIGNMENT;
                    break;
                case ObjectTypes.TOPIC:
                    vsType = ViewStateTypesEnum.TOPIC;
                    break;
                case ObjectTypes.SCRIPT:
                    vsType = ViewStateTypesEnum.SCRIPT;
                    break;
                default:
                    vsType = ViewStateTypesEnum.SYSTEM;
                    break;
            }
        }

        switch (vsType) {
            case ViewStateTypesEnum.TOPIC:
                treeDataSource.data = getTopicsTreeLikeData(records as ResultGroup<StorytellingTopicRecord>);
                break;
            case ViewStateTypesEnum.ASSIGNMENT:
                treeDataSource.data = getAssignmentsTreeLikeData(records as ResultGroup<StorytellingAssignmentRecord>);
                break;
            case ViewStateTypesEnum.SCRIPT:
                treeDataSource.data = getStoriesTreeLikeData(records as ResultGroup<StorytellingStoryRecord>);
                break;
            case ViewStateTypesEnum.SEARCH:
                treeDataSource.data = getSearchResultsTreeLikeData(records as ResultGroup<StorytellingRecord>);
                break;
            default:
                [];
        }
        if (!treeControl?.dataNodes) return null;
        treeControl.expandAll();
        treeControl.dataNodes.forEach(node => {
            const state = nodesExpandState[node.id];
            if (state != null) {
                if (state === false) {
                    treeControl.collapseDescendants(node);
                } else {
                    treeControl.expandDescendants(node);
                }
            }
        });
        return { treeDataSource, treeControl };
    }
);

export const selectTreeDataSource = createSelector(selectTreeDataSourceControlPair, pair => pair?.treeDataSource);
export const selectTreeControl = createSelector(selectTreeDataSourceControlPair, pair => pair?.treeControl);

export const selectSelectedObject = createSelector(selectTreeControl, selectCurrentNodeUuid, (treecontrol, uuid) => {
    return treecontrol?.dataNodes?.find(node => node.uniqueId === uuid)?.object || null;
});

// TODO Fix for other object types
export const selectTotalGroupedItem = createSelector(selectTreeControl, treeControl => {
    if (!treeControl) return null;
    const level = extractLowestNodeLevel(treeControl?.dataNodes);
    return treeControl?.dataNodes?.filter(node => node.level === level);
});
export const selectTotalGroupedItemCount = createSelector(selectTotalGroupedItem, totalGroupedItems => {
    return totalGroupedItems?.length || 0;
});
export const selectCurrentNodeIndex = createSelector(selectTreeControl, selectCurrentNodeUuid, selectCurrentViewState, (treeControl, currentNodeUuid, vs) => {
    if (!currentNodeUuid) {
        return 0;
    }

    const baseLvl = vs?.viewStateType === ViewStateTypesEnum.ASSIGNMENT ? 2 : 1;
    let uniqueIdToSearchFor = treeControl?.dataNodes?.find(item => item?.uniqueId === currentNodeUuid)?.uniqueId;
    if (!uniqueIdToSearchFor) {
        uniqueIdToSearchFor = treeControl?.dataNodes?.find(item => (item?.object as StorytellingRecord)?.Guid && currentNodeUuid.includes((item?.object as StorytellingRecord).Guid))?.uniqueId;
    }
    if (!uniqueIdToSearchFor) {
        return 0;
    }
    let idx = 0;
    treeControl.dataNodes.find(item => {
        if (item.level === baseLvl) {
            idx++;
        }
        return item?.uniqueId === uniqueIdToSearchFor;
    });
    return idx;
});

export const selectCurrentNormalCardItemHeights = createSelector(selectTreeControl, selectCurrentViewState, (treeControl, vs) => {
    if (!vs || !treeControl?.dataNodes) return [];
    const objtype = getObjectTypeByViewStateType(vs?.viewStateType);
    const heights = treeControl.dataNodes
        .filter(node => (node.level > 0 ? treeControl.isExpanded(node) : true))
        .map(item => {
            switch (item.level) {
                case 0:
                    return GROUP_HEADER_HEIGHT;
                case 1: {
                    if (item.object.Type === ObjectTypes.TOPIC || item.object.Type === ObjectTypes.SCRIPT) {
                        if (item.nodeLastInGroup) {
                            return CARD_LAST_ITEM_HEIGHT;
                        }
                        return CARD_HEIGHT;
                    }
                    return 34;
                }
                default: {
                    if (item.nodeFirstInGroup && item.nodeLastInGroup) {
                        if (objtype === ObjectTypes.ASSIGNMENT) {
                            return CARD_LAST_ITEM_HEIGHT;
                        }
                    }
                    if (item.nodeLastInGroup) {
                        return CARD_LAST_ITEM_HEIGHT;
                    }
                    if (item.nodeFirstInGroup) {
                        if (objtype === ObjectTypes.ASSIGNMENT) {
                            return CARD_HEIGHT;
                        }
                    }
                    return CARD_HEIGHT;
                }
            }
        })
        .filter(height => height > 0);
    return heights;
});

export const selectCurrentCondesedItemHeights = createSelector(selectTreeControl, treeControl => {
    if (!treeControl) return [];
    const heights = treeControl.dataNodes
        .filter(node => (node.level > 0 ? treeControl.isExpanded(node) : true))
        .map(item => {
            switch (item.level) {
                case 0:
                    return GROUP_HEADER_HEIGHT;
                default: {
                    if (item.nodeLastInGroup && item.nodeFirstInGroup) {
                        return CONDENSED_LAST_ITEM_HEIGHT + CONDENSED_FIRST_ITEM_HEADER_HEIGHT;
                    }
                    if (item.nodeLastInGroup) {
                        return CONDENSED_LAST_ITEM_HEIGHT;
                    }
                    if (item.nodeFirstInGroup) {
                        return CONDENSED_CARD_HEIGHT + CONDENSED_FIRST_ITEM_HEADER_HEIGHT;
                    }
                    return CONDENSED_CARD_HEIGHT;
                }
            }
        });
    return heights;
});

export const selectTileItemSize = createSelector(() => {
    return 146;
});

export const selectCurrentCardItemCustomFieldsHeights = createSelector(selectTreeControl, selectCurrentViewState, (treeControl, vs) => {
    if (!vs || !treeControl?.dataNodes) return [];
    const heights = treeControl.dataNodes
        .filter(node => (node.level > 0 ? treeControl.isExpanded(node) : true))
        .map(item => {
            switch (item.level) {
                case 0:
                    return GROUP_HEADER_HEIGHT;
                case 1: {
                    switch (item.object.Type) {
                        case ObjectTypes.TOPIC: {
                            if (item.nodeLastInGroup) {
                                return CARD_LAST_ITEM_HEIGHT + CUSTOM_FIELDS_ADDED_HEIGHT;
                            }
                            return CARD_HEIGHT + CUSTOM_FIELDS_ADDED_HEIGHT;
                        }
                        case ObjectTypes.SCRIPT: {
                            if (item.nodeLastInGroup) {
                                return CARD_LAST_ITEM_HEIGHT;
                            }
                            return CARD_HEIGHT;
                        }
                        default: {
                            return GROUP_HEADER_HEIGHT;
                        }
                    }
                }
                default: {
                    if (item.object.Type === ObjectTypes.ASSIGNMENT) {
                        if (item.nodeLastInGroup) {
                            return CARD_LAST_ITEM_HEIGHT;
                        }
                        return CARD_HEIGHT;
                    }
                    return CARD_HEIGHT;
                }
            }
        })
        .filter(height => height > 0);
    return heights;
});

export const selectCurrentCardItemHeights = createSelector(
    selectCurrentNormalCardItemHeights,
    selectCurrentCondesedItemHeights,
    selectCurrentCardItemCustomFieldsHeights,
    selectCurrentViewState,
    (normal, condensed, customFields, currentVs) => {
        if (!currentVs) return [];
        if (currentVs?.viewStateLayout?.Value === ViewStateLayouts.CONDENSE) {
            return condensed;
        }

        if (currentVs.viewStateType === ViewStateTypesEnum.PUBLICATION || currentVs.viewStateType === ViewStateTypesEnum.TOPIC || currentVs.viewStateType === ViewStateTypesEnum.SEARCH) {
            if ((currentVs as SingleViewStateRecord)?.viewStateCustomCardItems?.length > 0) {
                return customFields;
            }
        }

        return normal;
    }
);

export const selectTileViewTemplate = createSelector(selectDateTimeFields, selectCurrentGroupBy, selectGroupByDateTimeField, (dateTimeFields, groupBy, isDateTime) => {
    return isDateTime ? 'dragdroptimeline' : 'dragdropgroups';
});

export const selectTileViewDateRange = createSelector(
    selectCustomFilterDefsDict,
    selectPersonalTimeZone,
    selectClientTimeZone,
    selectDayOfWeek,
    selectCurrentViewState,
    selectCurrentApplication,
    (customFilterDefsDict, personalTimeZone, clientTimeZone, dayOfWeek, vs, application) => {
        if (application === 'playbook') {
            const targetDateFilterDef = <FilterDef>(<SingleViewStateRecord>vs).viewStateFilter.map.PSTTargetDate;
            if (!targetDateFilterDef) return null;
            const targetDate = targetDateFilterDef.Value;

            const firstDay: DateTime = new DateTime();
            const lastDay: DateTime = new DateTime();

            let fromDate: Date = null;
            let toDate: Date = null;

            if (targetDate) {
                const dateRange = targetDate.get();
                let value: string = null;

                if (targetDate && targetDate.custom && targetDate.custom.startsWith('@date_range_')) {
                    const id: string = targetDate.custom.replace('@date_range_', '').replace(/_/g, '').toUpperCase();

                    if (customFilterDefsDict[id]) {
                        if (customFilterDefsDict[id].Validation === 'DATE' || customFilterDefsDict[id].Validation === 'TIME' || customFilterDefsDict[id].Validation === 'MOSDATETIME') {
                            value = customFilterDefsDict[id].Value;
                        }
                    }
                }
                if (value !== 'thisweek' && value !== 'weekafternext' && value !== 'nextweek') {
                    if (value === 'all') {
                        fromDate = moment().startOf('isoWeek').add(dayOfWeek, 'days').subtract(1, 'day').startOf('day').toDate();
                        toDate = moment().startOf('isoWeek').add(dayOfWeek, 'days').subtract(1, 'day').add(6, 'days').endOf('day').toDate();
                    } else {
                        if (dateRange[0].getDay() === dayOfWeek) {
                            fromDate = moment(dateRange[0]).startOf('day').toDate();
                            toDate = moment(dateRange[1]).endOf('day').toDate();
                        } else {
                            fromDate = moment(dateRange[0]).startOf('isoWeek').add(dayOfWeek, 'days').subtract(1, 'day').startOf('day').toDate();
                            toDate = moment(dateRange[0]).startOf('isoWeek').add(dayOfWeek, 'days').subtract(1, 'day').add(6, 'days').endOf('day').toDate();

                            if (toDate.getFullYear() === 1800) {
                                fromDate = moment().startOf('isoWeek').add(dayOfWeek, 'days').subtract(1, 'day').startOf('day').toDate();
                                toDate = moment().startOf('isoWeek').add(dayOfWeek, 'days').subtract(1, 'day').add(6, 'days').endOf('day').toDate();
                            }
                        }
                    }
                } else {
                    fromDate = moment(dateRange[0]).startOf('day').toDate();
                    toDate = moment(dateRange[1]).endOf('day').toDate();
                }
            } else {
                fromDate = moment().startOf('isoWeek').add(dayOfWeek, 'days').subtract(1, 'day').toDate();
                toDate = moment().startOf('isoWeek').add(dayOfWeek, 'days').subtract(1, 'day').add(7, 'days').toDate();
            }

            firstDay.set(fromDate);
            lastDay.set(toDate);

            return { firstDate: firstDay, lastDate: lastDay };
        }
        return null;
    }
);

export const selectTotalGroupCount = createSelector(selectGroupedSortedTopics, topics => {
    return topics.keys.length;
});

export const selectTileViewColumns = createSelector(
    selectGroupedSortedTopics,
    selectTileViewDateRange,
    selectDayOfWeek,
    selectStartEndIndex,
    selectTileViewTemplate,
    selectCurrentViewState,
    selectI18N,
    (topics, dateRange, dayOfWeek, columnRange, template, vs, i18n) => {
        const columns = new Array<TileColumn>();

        if (vs.viewStateType !== ViewStateTypesEnum.TOPIC) {
            return null;
        }

        if (template === 'dragdroptimeline') {
            if (!dateRange) return columns;

            const temp = new Array<TileDay>();

            temp.push(new TileDay(i18n?.PSTWebSunday, i18n?.PSTWebSUN, [], 0, null, null));
            temp.push(new TileDay(i18n?.PSTWebMonday, i18n?.PSTWebMON, [], 0, null));
            temp.push(new TileDay(i18n?.PSTWebTuesday, i18n?.PSTWebTUE, [], 0, null));
            temp.push(new TileDay(i18n?.PSTWebWednesday, i18n?.PSTWebWED, [], 0, null, null));
            temp.push(new TileDay(i18n?.PSTWebThursday, i18n?.PSTWebTHU, [], 0, null, null));
            temp.push(new TileDay(i18n?.PSTWebFriday, i18n?.PSTWebFRI, [], 0, null, null));
            temp.push(new TileDay(i18n?.PSTWebSaturday, i18n?.PSTWebSAT, [], 0, null, null));

            temp.forEach((_, i) => {
                const col = temp[(i + dayOfWeek) % temp.length];
                columns.push(col);
            });

            const current = dateRange.firstDate.deepCopy();
            columns.forEach(col => {
                const column = <TileDay>col;
                column.day = current.moment.date();
                column.month = current.moment.month();
                column.year = current.moment.year();

                let key = current.get().toISOString();
                const dateVal = new Date(key);
                key = DateTime.handleDate(dateVal);
                column.value = key;

                current.set(current.moment.add(1, 'days').toDate());
            });
        }

        if (template === 'dragdropgroups') {
            topics.keys.slice(columnRange.startIndex, columnRange.endIndex + 1).forEach(key => {
                columns.push(new TileColumn(key.value, topics.groupDisplay[key.value], []));
            });
            columns.forEach(col => {
                const column = <TileColumn>col;
                column.value = column.name;
            });
        }
        return columns;
    }
);

// TODO: Separate this in its own file
export const selectRecordMarkers = createSelector(
    selectTopicsWithAssignmentsAndStories,
    selectAssignmentRecordsMap,
    selectListNewGuids,
    selectListGuids,
    (topics, assignments, newGuids, listGuids) => {
        return [...newGuids, ...listGuids].reduce((acc, guids) => {
            const topic = topics.get(guids);
            if (!topic) {
                return acc;
            }
            const records = [topic, ...(topic.children || [])];
            if (!records?.length) {
                return acc;
            }
            records.forEach(rec => {
                if (rec?.PSTTopicAddress?.lat && rec?.PSTTopicAddress.formattedAddress) {
                    const markerData: StorytellingMarker = {
                        guids: [rec.Guid],
                        latitude: Number(rec.PSTTopicAddress?.lat),
                        longitude: Number(rec.PSTTopicAddress?.long),
                        position: new google.maps.LatLng(Number(rec.PSTTopicAddress.lat), Number(rec.PSTTopicAddress.long)),
                    };
                    const existingMarker = acc.find(marker => marker.latitude === markerData.latitude && marker.longitude === markerData.longitude);
                    if (existingMarker) {
                        if (existingMarker.labelOptions) {
                            existingMarker.labelOptions.text = String(Number(existingMarker.labelOptions.text) + 1);
                        } else {
                            existingMarker.labelOptions = {
                                text: '2',
                                color: 'white',
                                fontSize: '12px',
                            };
                        }
                        existingMarker.guids.push(rec.Guid);
                    } else {
                        acc.push(markerData);
                    }
                    return acc;
                }
            });
            return acc;
        }, [] as StorytellingMarker[]);
    }
);

export const selectUserMarkers = createSelector(selectUserRecords, selectCurrentViewState, (userRecords, currentVs) => {
    if (!currentVs) {
        return [];
    }
    if (currentVs.viewStateLayout.Value !== ViewStateLayouts.MAP) {
        return [];
    }

    if (currentVs.viewStateType !== ViewStateTypesEnum.TOPIC) {
        return [];
    }

    if (!(currentVs as TopicViewStateRecord).viewStateMapShowTeamLocations) {
        return [];
    }

    return [...userRecords].reduce((acc, user) => {
        if (!user) {
            return acc;
        }
        if (user?.RSLocation?.getLastKnownLocation()?.latitude) {
            const markerData: StorytellingMarker = {
                guids: [user.Guid],
                latitude: Number(user.RSLocation.getLastKnownLocation().latitude),
                longitude: Number(user.RSLocation.getLastKnownLocation().longitude),
                position: new google.maps.LatLng(Number(user.RSLocation.getLastKnownLocation().latitude), Number(user.RSLocation.getLastKnownLocation().longitude)),
                iconUrl: {
                    url: 'https://img.icons8.com/metro/50/000000/street-view.png',
                    scaledSize: {
                        width: 32,
                        height: 32,
                    },
                },
            };
            const existingMarker = acc.find(marker => marker.latitude === markerData.latitude && marker.longitude === markerData.longitude);
            if (existingMarker) {
                if (existingMarker.labelOptions) {
                    existingMarker.labelOptions.text = String(Number(existingMarker.labelOptions.text) + 1);
                } else {
                    existingMarker.labelOptions = {
                        text: '2',
                        color: 'white',
                        fontSize: '12px',
                    };
                }
                existingMarker.guids.push(user.Guid);
            } else {
                acc.push(markerData);
            }
            return acc;
        }
        return acc;
    }, [] as StorytellingMarker[]);
});

export const selectAllMarkers = createSelector(selectUserMarkers, selectRecordMarkers, (userMarkers, recordMarkers) => {
    return [...userMarkers, ...recordMarkers];
});

export const selectAllRecords = createSelector(
    selectTopicsWithAssignmentsAndStories,
    selectAssignmentRecordsMap,
    selectStoryRecordsMap,
    selectUserRecordsMap,
    (topics, assignments, stories, users) => {
        return new Map<string, StorytellingRecord>([...topics, ...assignments, ...stories, ...users]);
    }
);

export const selectPlaybookListRecords = createSelector(
    selectListGuids,
    selectTopicRecordsMap,
    selectAssignmentRecordsMap,
    selectStoryRecordsMap,
    selectRundownRecordsMap,
    (listGuids, topicsMap, assignmentsMap, storiesMap, rundownsMap) => {
        const topics: Array<StorytellingTopicRecord> = [];
        const assignments: Array<StorytellingAssignmentRecord> = [];
        const stories: Array<StorytellingStoryRecord> = [];
        const rundowns: Array<StorytellingRundownRecord> = [];
        listGuids.forEach(guid => {
            const record = topicsMap.get(guid) || assignmentsMap.get(guid) || storiesMap.get(guid) || rundownsMap.get(guid);
            switch (record?.Type) {
                case ObjectTypes.TOPIC:
                    topics.push(record);
                    break;
                case ObjectTypes.ASSIGNMENT:
                    assignments.push(record);
                    break;
                case ObjectTypes.SCRIPT:
                    stories.push(record);
                    break;
                case ObjectTypes.RUNDOWN:
                    rundowns.push(record);
                    break;
            }
        });
        return {
            topics,
            assignments,
            stories,
            rundowns,
        };
    }
);
